import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { take, timer } from 'rxjs';
import { AppState } from '../app.state';
import { MediaContent } from '../objects/media-player/media-content';
import { MediaRadio } from '../objects/media-player/media-radio';
import {
  pauseMedia,
  resumeMedia,
  updateMediaContentGeneral,
} from '../player.actions';

@Injectable({
  providedIn: 'root',
})
export class RadioMetadataFetcherService {
  metadataStatsListener: any;

  constructor(private store: Store<AppState>, private httpClient: HttpClient) {
    // initial state when the page loads
    const session = this.mediaSession;
    if (session) {
      session.playbackState = 'none';
    }
  }

  /**
   * `navigator.mediaSession` where it exists. Null during prerendering (no
   * `navigator.mediaSession` in Node) and on browsers without the API.
   */
  private get mediaSession(): MediaSession | null {
    return typeof navigator !== 'undefined' && 'mediaSession' in navigator
      ? navigator.mediaSession
      : null;
  }

  async stopAndStartRadioMetadataWithLibs(
    mediaContent: MediaContent,
    urlToUse: string
  ) {
    let radio = mediaContent as MediaRadio;
    let url = urlToUse;
    // Metadata listener.
    // Stop previous listener!
    this.stopRadioMetadataWithLibs();
    // Heavy parsers — pulled in on demand so they stay out of the initial bundle.
    const { default: IcecastMetadataStats } = await import(
      'icecast-metadata-stats'
    );
    this.metadataStatsListener = new IcecastMetadataStats(url, {
      onStats: (stats: any) => {
        if (stats != undefined && stats.icy != null) {
          this.store.dispatch(
            updateMediaContentGeneral({
              currentStreamTitle: stats.icy.StreamTitle,
              currentStreamImageUrl:
                stats.icy.StreamUrl != undefined ? stats.icy.StreamUrl : '',
              playerState: null,
              progress: null,
              mediaFormat: null,
              audioChannels: null,
              bitrateFromSource: null,
              sampleFrequency: null,
            })
          );
          this.setBrowserMetadata(
            radio.name,
            stats.icy.StreamTitle,
            radio.country,
            stats.icy.StreamUrl != undefined
              ? stats.icy.StreamUrl
              : radio.iconUrl
          );
        } else {
          // Stop previous listener!
          this.stopRadioMetadataWithLibs();
          this.store.dispatch(
            updateMediaContentGeneral({
              currentStreamTitle: '',
              currentStreamImageUrl: '',
              playerState: null,
              progress: null,
              mediaFormat: null,
              audioChannels: null,
              bitrateFromSource: null,
              sampleFrequency: null,
            })
          );
          this.setBrowserMetadata(
            radio.name,
            'Internet Radio',
            radio.country,
            radio.iconUrl
          );
        }
      },
      onError: (error: any) => {
        console.log('error: ', error);
        // Stop previous listener!
        this.stopRadioMetadataWithLibs();
        this.store.dispatch(
          updateMediaContentGeneral({
            currentStreamTitle: '',
            currentStreamImageUrl: '',
            playerState: null,
            progress: null,
            mediaFormat: null,
            audioChannels: null,
            bitrateFromSource: null,
            sampleFrequency: null,
          })
        );
        this.setBrowserMetadata(
          radio.name,
          'Internet Radio',
          radio.country,
          radio.iconUrl
        );
      },
      interval: 30,
      // enableLogging: true,
      // icyMetaInt: 16000,
      // icyDetectionTimeout: 0,
      // : ['icy', 'ogg', 'icestats', 'stats', 'nextsongs'],
      // ToDo - some radios use 'icestats', maybe detect and fetch like that ?
      sources: ['icy'],
    });
    this.metadataStatsListener.start();

    // Technical metadata stuff.
    try {
      const aborter = new AbortController();
      const response = await fetch(url, { signal: aborter.signal });
      await timer(500).pipe(take(1)).toPromise();
      const webStream = response.body;
      const { parseWebStream } = await import('music-metadata');
      const metadata = await parseWebStream(webStream!!);
      aborter.abort();
      // Log the parsed metadata
      this.store.dispatch(
        updateMediaContentGeneral({
          currentStreamTitle: null,
          currentStreamImageUrl: null,
          playerState: null,
          progress: null,
          mediaFormat: metadata.format.codec,
          audioChannels: metadata.format.numberOfChannels,
          bitrateFromSource: metadata.format.bitrate,
          sampleFrequency: metadata.format.sampleRate,
        })
      );
    } catch (e: unknown) {
      console.log('e: ', e);
      this.store.dispatch(
        updateMediaContentGeneral({
          currentStreamTitle: null,
          currentStreamImageUrl: null,
          playerState: null,
          progress: null,
          mediaFormat: '',
          audioChannels: 0,
          bitrateFromSource: 0,
          sampleFrequency: 0,
        })
      );
    }
  }

  stopRadioMetadataWithLibs() {
    this.metadataStatsListener?.stop();
    this.metadataStatsListener = null;
  }

  private async startRadioMetadataManually(url: string) {
    const myHeaders = new Headers({
      'Icy-Metadata': '1',
      'Content-Type': 'application/x-www-form-urlencoded',
      Connection: 'close',
    });
    const aborter = new AbortController();
    const response = await fetch(
      url.includes('https') ? url : url.replace('http', 'https'),
      { signal: aborter.signal, headers: myHeaders }
    );
    await timer(500).pipe(take(1)).toPromise();
    const body = response.body;
    console.log('body: ', body);

    aborter.abort();

    let reader = body?.getReader();

    let charsReceived = 0;
    let result = '';
    reader?.read().then(function processText({ done, value }): any {
      // Result objects contain two properties:
      // done  - true if the stream has already given you all its data.
      // value - some data. Always undefined when done is true.
      if (done) {
        console.log('Stream complete; value: ', value);
        return;
      }

      // value for fetch streams is a Uint8Array
      charsReceived += value.length;
      const chunk = value;
      let listItem = document.createElement('li');
      listItem.textContent = `Received ${charsReceived} characters so far. Current chunk = ${chunk}`;

      result += chunk;

      // Read some more, and call this function again
      return reader.read().then(processText);
    });

    // const string = await new Response(body).text();
    // console.log('string: ', string);

    /*
    const toCancel = this.httpClient
      .get(url, {
        responseType: 'text',
        observe: 'response',
        headers: {
          // Connection: 'close',
          // 'Access-Control-Allow-Headers': 'Access-Control-Allow-Methods, Access-Control-Allow-Origin, Icy-Metadata, mode, accept, Content-Type',
          // 'Access-Control-Allow-Methods': 'GET, OPTIONS',
          // 'Access-Control-Allow-Origin': '*',
          // 'Icy-Metadata': '1',
          'Content-Type': 'application/x-www-form-urlencoded',
          // accept: 'text/plain',
          // mode: 'no-cors',
          // Range: 'bytes=0-4080',
        },
      })
      .pipe(timeout(3000))
      .subscribe((buffer: any) => {
        console.log('buffer: ', buffer);
        console.log('Response status:', buffer.status);
        console.log('Body:', buffer.body);
      });
    */

    /*
    const req = new XMLHttpRequest();
    req.open('GET', url, true);
    req.responseType = 'arraybuffer';

    req.onload = (event) => {
      console.log('event: ', event);
      const arrayBuffer = req.response; // Note: not req.responseText
      if (arrayBuffer) {
        const byteArray = new Uint8Array(arrayBuffer);
        console.log('byteArray: ', byteArray);
        byteArray.forEach((element, index) => {
          // do something with each byte in the array
        });
      }
    };
    req.timeout = 1000;
    req.send(null);
    */

    /*
    setInterval(() => {
      // req.abort();
      toCancel.unsubscribe();
      toCancel.closed = true;
    }, 3000);
    */

    return null;
  }

  private setBrowserMetadata(
    title: string,
    artist: string,
    album: string,
    artworkUrl: string
  ) {
    const session = this.mediaSession;
    if (!session) {
      return;
    }

    session.metadata = new MediaMetadata({
      title: title,
      artist: artist,
      album: album,
      artwork: [{ src: artworkUrl }],
    });

    // Yes, I used the same thumbnail for all sizes because I'm lazy

    session.setActionHandler('play', () => {
      this.store.dispatch(resumeMedia());
      session.playbackState = 'playing'; // ToDo - do this from the media-service as well!
    });

    session.setActionHandler('pause', () => {
      this.store.dispatch(pauseMedia());
      session.playbackState = 'paused'; // ToDo - do this from the media-service as well!
    });

    session.setActionHandler('previoustrack', () => {
      // NOOP
      // playPreviousSong();
      session.playbackState = 'playing';
    });

    session.setActionHandler('nexttrack', () => {
      // NOOP
      // playNextSong();
      // session.playbackState = 'playing';
    });
  }
}
